import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs'

import { CustomSensorPrefix, TelemetryReadMode, Vehicle } from '@ti/contracts'
import { RangeMs } from '@ti/core/common/contracts'
import { VehicleTelemetry } from '@ti/core/contracts/vehicle'

import { CustomSensorsProvider } from './custom-sensors.provider'
import { FlespiClient, HistoricalTelemetryEntry } from './flespi.client'

export interface HistoricalTelemetryState {
  /** Telemetry keyed by flespi device id, representing the state at currentTime. */
  telemetryByDevice: Map<number, VehicleTelemetry>
  /** Whether the initial page load is still in progress. */
  isLoading: boolean
}

/**
 * Loads historical telemetry for a fixed set of vehicles and a range of time, keeping a full
 * per-device timeline of every message received (each carrying forward the latest known value
 * of every parameter, same as flespi's own real-time semantics). `currentTime` is a pure query
 * over that timeline, so scrubbing via `setCurrentTime` never re-fetches.
 *
 * Usage:
 *  1. Call `initialize(vehicles, currentTime, totalRange)`.
 *  2. Subscribe to `state$` to receive incremental updates as pages are loaded.
 *  3. Call `setCurrentTime(time)` to scrub within the already-loaded range.
 *  4. Call `destroy()` when the historical view is closed to cancel in-flight loading.
 *
 * Not an Angular-injectable: constructed and owned directly by FleetStore per historical
 * session, so its dependencies are passed in explicitly rather than via `inject()`.
 */
export class HistoricalTelemetryProvider {
  private readonly _state$ = new BehaviorSubject<HistoricalTelemetryState>({
    telemetryByDevice: new Map(),
    isLoading: false,
  })

  /** Emits the current historical telemetry state after each loaded page or scrub. */
  public get state$(): Observable<HistoricalTelemetryState> {
    return this._state$.asObservable()
  }

  /** Synchronous snapshot of the current state. */
  public get state(): HistoricalTelemetryState {
    return this._state$.getValue()
  }

  private readonly flespiClient: FlespiClient
  private readonly customSensorsProvider: CustomSensorsProvider

  private abortController?: AbortController
  private runGeneration = 0
  private currentTime = 0
  private readonly timelineByDevice = new Map<number, HistoricalTelemetryEntry[]>()

  constructor(flespiClient: FlespiClient, customSensorsProvider: CustomSensorsProvider) {
    this.flespiClient = flespiClient
    this.customSensorsProvider = customSensorsProvider
  }

  /**
   * Starts loading historical telemetry for `vehicles` over `totalRange`, evaluated at
   * `currentTime`. Any previous in-flight load is cancelled before starting.
   *
   * @param vehicles    Vehicles to load telemetry for.
   * @param currentTime Point-in-time (ms) to evaluate the telemetry state at.
   * @param totalRange  Full historical range [startMs, endMs].
   */
  public async initialize(
    vehicles: Vehicle[],
    currentTime: number,
    totalRange: RangeMs,
    options?: { telemetryReadMode?: TelemetryReadMode; isCurrent?: () => boolean },
  ): Promise<void> {
    this.cancelLoad()
    const runGeneration = ++this.runGeneration
    this.timelineByDevice.clear()
    this.currentTime = currentTime

    if (!vehicles.length) {
      this._state$.next({ telemetryByDevice: new Map(), isLoading: false })
      return
    }

    this.abortController = new AbortController()
    const signal = this.abortController.signal

    this._state$.next({ telemetryByDevice: new Map(), isLoading: true })

    try {
      const customSensors = await firstValueFrom(this.customSensorsProvider.state$.customSensors)
      if (!this.isRunCurrent(runGeneration, signal, options?.isCurrent)) return
      const customSensorFields = customSensors.map((sensor) => `${CustomSensorPrefix}${sensor.id}`)

      await this.flespiClient.loadHistoricalTelemetry(
        vehicles,
        totalRange,
        customSensorFields,
        (entries) => {
          if (this.isRunCurrent(runGeneration, signal, options?.isCurrent)) {
            this.applyEntries(entries)
          }
        },
        signal,
        options,
      )
    } catch (e) {
      if (!this.isRunCurrent(runGeneration, signal, options?.isCurrent)) return
      if (options?.telemetryReadMode) throw e
      console.error('HistoricalTelemetryProvider: error loading historical telemetry', e)
    } finally {
      if (this.isRunCurrent(runGeneration, signal, options?.isCurrent)) {
        this._state$.next({ ...this._state$.getValue(), isLoading: false })
      }
    }
  }

  /**
   * Moves the query point-in-time within the already-loaded timeline and re-emits the
   * corresponding snapshot. Purely local - does not trigger any network requests.
   */
  public setCurrentTime(currentTime: number): void {
    this.currentTime = currentTime
    this._state$.next({
      ...this._state$.getValue(),
      telemetryByDevice: this.computeSnapshotMap(),
    })
  }

  /**
   * Cancels any in-flight load and resets the state.
   * Call this when the historical view is closed.
   */
  public destroy(): void {
    this.runGeneration++
    this.cancelLoad()
    this.timelineByDevice.clear()
    this._state$.next({ telemetryByDevice: new Map(), isLoading: false })
  }

  private cancelLoad(): void {
    this.abortController?.abort()
    this.abortController = undefined
  }

  private isRunCurrent(
    runGeneration: number,
    signal: AbortSignal,
    isCurrent?: () => boolean,
  ): boolean {
    return this.runGeneration === runGeneration && !signal.aborted && (isCurrent?.() ?? true)
  }

  /**
   * Appends newly-loaded timeline entries (already sorted ascending by timestamp within each
   * device, since pages are fetched in ascending order) and re-emits the snapshot for the
   * current query time.
   */
  private applyEntries(entries: HistoricalTelemetryEntry[]): void {
    entries.forEach((entry) => {
      let deviceTimeline = this.timelineByDevice.get(entry.deviceId)
      if (!deviceTimeline) {
        deviceTimeline = []
        this.timelineByDevice.set(entry.deviceId, deviceTimeline)
      }
      deviceTimeline.push(entry)
    })

    this._state$.next({
      ...this._state$.getValue(),
      telemetryByDevice: this.computeSnapshotMap(),
    })
  }

  /** Builds the per-device telemetry snapshot at `this.currentTime` from the loaded timeline. */
  private computeSnapshotMap(): Map<number, VehicleTelemetry> {
    const telemetryByDevice = new Map<number, VehicleTelemetry>()
    this.timelineByDevice.forEach((deviceTimeline, deviceId) => {
      const entry = findLatestEntryAtOrBefore(deviceTimeline, this.currentTime)
      if (entry) {
        telemetryByDevice.set(deviceId, entry.telemetry)
      }
    })
    return telemetryByDevice
  }
}

/** Binary-searches the last entry with `timestamp <= time` in a timeline sorted ascending. */
function findLatestEntryAtOrBefore(
  entries: HistoricalTelemetryEntry[],
  time: number,
): HistoricalTelemetryEntry | undefined {
  let lo = 0
  let hi = entries.length - 1
  let result: HistoricalTelemetryEntry | undefined

  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (entries[mid].timestamp <= time) {
      result = entries[mid]
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  return result
}
